import { KeyboardEvent , useEffect, useRef, useState } from "react";
import { inputFormHeightOption, titleMRClass } from "src/components/common/common";
import { createPortal } from "react-dom";
import DatePicker from "src/components/common/calendar/DatePicker";
import { formatDate, formatDateWithTime } from "src/utils/string/stringUtil";
import { handleKeyDown } from "src/utils/commonForm/commonFormUtils";

interface Props {
	title?: string;
	titleWidth?: number;
	value: Date | undefined;			setValue: (value: Date | undefined) => void;
	editable: boolean;
	titleless?: boolean;
	visible: boolean;		setVisible: (visible: boolean) => void;
	name?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	wrapperOption?: {
		wrapperStyle: string;
		titleStyle: string;
		inputStyle: string;
		timeInputStyle?: string;
	}
	pickerPos?: {top: number, left: number, height: number};
	setPickerPos?: (pickerPos: {top: number, left: number, height: number}) => void;
	resetType?: 'today' | 'clear';
	yearMonthVisible?: boolean;
	timeEditable?: boolean;
	placeholder?: string;
}

export const SingleDateForm = (props: Props) => {
	const { 
		title,
		titleWidth,
		value, setValue,
		editable,
		titleless = false,
		visible, setVisible,
		name,	
		onChange,
		wrapperOption = {
			wrapperStyle: "w-[350px] min-w-[350px] mr-[10px] mb-[5px]",
			titleStyle: "w-[150px] min-w-[150px] text-left",
			inputStyle: "w-[200px] min-w-[200px]",
			timeInputStyle: "w-[100px] min-w-[100px]"
		},
		pickerPos = {top: 0, left: 0, height: 0},
		setPickerPos = () => { console.log('setPickerPos를 호출해서 top, left, height를 설정해야 합니다') },
		resetType = 'today',
		yearMonthVisible = false,
		timeEditable = false,
		placeholder = "날짜 선택"
	} = props;

	const inputRef = useRef<HTMLInputElement>(null);
	const datePickerContainerRef = useRef<HTMLDivElement>(null);
	const [isEditing, setIsEditing] = useState(false);
	const [inputValue, setInputValue] = useState(formatDate(value));
	const [inputTimeValue, setInputTimeValue] = useState(formatDate(value));

	//visible 상태가 변경될 때 달력 컨테이너에 포커스 주기
	useEffect(() => {
		if (visible && datePickerContainerRef.current) {
			datePickerContainerRef.current.focus();
		}
	}, [visible]);

	// 출고일자 입력란 클릭 시 위치 계산
	const handleDateInputClick = () => {
		if (!editable) return;
		if (inputRef.current) {
			const rect = inputRef.current.getBoundingClientRect();
			setPickerPos({ top: rect.bottom + 4, left: rect.left, height: rect.height });
		}
		setVisible(true);
	};

	// 날짜 입력란 변경 시 동작 > 달력 클릭할때의 이벤트가 아님
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
	};

	// 시간 입력란 변경 시 동작
	const handleInputTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		// const [hour, minute, second] = e.target.value.split(':').map(Number);
		// const newDate = new Date(value ?? new Date());
		// newDate.setHours(hour, minute, second);
		// setValue(newDate);
		console.log('handleInputTimeChange : ', e.target.value);
		setInputTimeValue(e.target.value);
	};

	const handleInputBlur = () => {
		setIsEditing(false);
		// 날짜 형식 검증 (YYYY-MM-DD)
		const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
		if (dateRegex.test(inputValue)) {
			const [year, month, day] = inputValue.split('-').map(Number);
			const newDate = new Date(year, month - 1, day);
			if (!isNaN(newDate.getTime())) {
				setValue(newDate);
			} else {
				setInputValue(formatDate(value));
			}
		} else {
			setInputValue(formatDate(value));
		}
	};

	//input에서 입력 기능이 활성화되고, 엔터키를 누를때의 동작
	const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.currentTarget.blur();
			if(visible) {
				setVisible(false);
				handleDatePickerKeyDown();
			}
		}
	};

	//visible 상태가 활성화되고, datePickerContainerRef.current.focus()가 된 후의의 엔터키를 누를때의 동작
	const handlePickerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter' || e.key === 'Escape') {
			e.preventDefault();
			setVisible(false);
			
			if (e.key === 'Enter') {
				handleDatePickerKeyDown();
			} else {
				inputRef.current?.focus();
			}
		}
	};

	const handleDatePickerKeyDown = () => {

		//입력 포커스 이동을 위한 임시 e : 이벤트 객체
		const syntheticEvent = {
			key: "Enter",
			preventDefault: () => { /* console.log("Synthetic event preventDefault called"); */ },
			currentTarget: inputRef.current,
			target: inputRef.current,
		} as unknown as KeyboardEvent<HTMLInputElement>;

		handleKeyDown(syntheticEvent);
	};

	const onClear =  resetType === 'clear' ? () => {
		setValue(undefined);
		setInputValue('');
		setVisible(false);
	} : undefined

	return (
		<div className={`flex h-[32px] ${wrapperOption.wrapperStyle}`} style={titleWidth ? { width: 'max-content' } : undefined}>
			<p className={`${wrapperOption.titleStyle} form-title ${titleMRClass} cursor-pointer ${titleless ? 'hidden' : ''}`} 
				style={titleWidth ? { width: titleWidth, minWidth: titleWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : undefined}
				title={title}
				onClick={() => {
					handleDateInputClick();
				}}
				tabIndex={ editable ? 0 : -1}
			>
				{title}
			</p>
			{isEditing ? (
				<>
					<input
						ref={inputRef}
						className={`${editable ? 'bg-white' : 'bg-gray-disable'} ${inputFormHeightOption} px-[6px] w-[100px] text-left rounded-[4px] border-[1px] ${wrapperOption.inputStyle}`}
						value={inputValue}
						onChange={handleInputChange}
						onBlur={handleInputBlur}
						onKeyDown={handleInputKeyDown}
						placeholder="YYYY-MM-DD"
						disabled={!editable}
						autoFocus
					/>
				</>
			) : (
				<p
					ref={inputRef}
					className={`${editable ? 'bg-white' : 'bg-gray-disable'} ${inputFormHeightOption} px-[6px] w-[100px] text-left rounded-[4px] border-[1px] cursor-pointer ${wrapperOption.inputStyle}`}
					onClick={handleDateInputClick}
					onDoubleClick={() => {
						setIsEditing(true);
						setInputValue(formatDate(value));
					}}
					tabIndex={ editable ? 0 : -1}
				>
					{value ? (timeEditable ? formatDateWithTime(value) : formatDate(value)) : placeholder}
				</p>
			)}
			{visible && createPortal(
				<>
					{/* 레이아웃 */}
					<div
						className="fixed top-0 left-0 w-screen h-screen z-[99998]"
						onClick={() => setVisible(false)}
					/>
					<div
						ref={datePickerContainerRef}
						style={{
							position: 'fixed', top: pickerPos.top, left: pickerPos.left, zIndex: 99999
						}}
						onClick={e => e.stopPropagation()}
						onKeyDown={handlePickerKeyDown}
						tabIndex={ editable ? 0 : -1}
					>
						<DatePicker
							value={value}
							onChange={(date: Date | undefined) => {
								if(date) {
									setValue(date);
									setInputValue(formatDate(date));
								}
							}}
							onReset={() => {
								const today = new Date();
								setValue(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
								setInputValue(formatDate(today));
								setVisible(false);
							}}
							//완료, 더블클릭 시 일어나는 동작
							onConfirm={(date: Date | undefined) => {
								// console.log('singleDateForm DatePicker onConfirm : ', new Date(date.getFullYear(), date.getMonth(), date.getDate()));
								// console.log('singleDateForm DatePicker formatDate : ', formatDate(date));
								if(date) {
									if(resetType === 'today') {
										setValue(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
										setInputValue(formatDate(date));
									} else {
										// 취소 시 보이지만 않게 처리
									}
									setVisible(false);
									handleDatePickerKeyDown();
								}
							}}
							onClear={onClear}
							yearMonthVisible={yearMonthVisible}
							timeEditable={timeEditable}
						/>
					</div>
				</>,
				document.body
			)}
		</div>
	)
}
